import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Scale, FileText, Shield, Bot, Lock } from "lucide-react";
import LegalDocumentDialog from "@/components/LegalDocumentDialog";
import TermsOfUseContent from "@/components/legal/TermsOfUse";
import PrivacyPolicyContent from "@/components/legal/PrivacyPolicy";
import LGPDComplianceContent from "@/components/legal/LGPDCompliance";
import AITermsContent from "@/components/legal/AITerms";
import SecurityInfoContent from "@/components/legal/SecurityInfo";

export default function Legal() {
  const [openDialog, setOpenDialog] = useState<string | null>(null);

  const legalDocuments = [
    {
      id: "terms",
      title: "Termos de Uso",
      description: "Condições gerais de uso da plataforma Justiçai",
      icon: FileText,
      content: <TermsOfUseContent />
    },
    {
      id: "privacy",
      title: "Política de Privacidade",
      description: "Como protegemos e utilizamos seus dados pessoais",
      icon: Shield,
      content: <PrivacyPolicyContent />
    },
    {
      id: "lgpd",
      title: "LGPD",
      description: "Conformidade com a Lei Geral de Proteção de Dados",
      icon: Scale,
      content: <LGPDComplianceContent />
    },
    {
      id: "ai-terms",
      title: "Termos de IA",
      description: "Condições específicas para uso da inteligência artificial",
      icon: Bot,
      content: <AITermsContent />
    },
    {
      id: "security",
      title: "Segurança",
      description: "Medidas de segurança e proteção implementadas",
      icon: Lock,
      content: <SecurityInfoContent />
    }
  ];

  const currentDocument = legalDocuments.find(doc => doc.id === openDialog);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Scale className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Informações Legais</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Acesse todos os documentos legais e informações sobre segurança da plataforma Justiçai
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {legalDocuments.map((document) => {
            const Icon = document.icon;
            return (
              <Card key={document.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Icon className="h-6 w-6 text-primary" />
                    {document.title}
                  </CardTitle>
                  <CardDescription>
                    {document.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => setOpenDialog(document.id)}
                    className="w-full"
                    variant="outline"
                  >
                    Ver Detalhes
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações Importantes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <p className="text-sm text-muted-foreground">
                <strong>Atualização:</strong> Nossos documentos legais são atualizados regularmente 
                para garantir conformidade com a legislação vigente.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <p className="text-sm text-muted-foreground">
                <strong>Transparência:</strong> Mantemos total transparência sobre como coletamos, 
                processamos e protegemos seus dados.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <p className="text-sm text-muted-foreground">
                <strong>Segurança:</strong> Implementamos as melhores práticas de segurança 
                para proteger suas informações e documentos.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <p className="text-sm text-muted-foreground">
                <strong>IA Responsável:</strong> Nossa inteligência artificial é desenvolvida 
                seguindo princípios éticos e de responsabilidade.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal para exibir documentos */}
      {currentDocument && (
        <LegalDocumentDialog
          isOpen={!!openDialog}
          onClose={() => setOpenDialog(null)}
          title={currentDocument.title}
        >
          {currentDocument.content}
        </LegalDocumentDialog>
      )}
    </div>
  );
}